/**
 * SentryUserSync — links Sentry sessions to the signed-in user.
 *
 * Calls `setUser()` from `@revalize/monitoring/frontend` whenever the API
 * hook's authentication state changes. Without this, Sentry breadcrumbs
 * and error reports can't be traced back to a specific customer when
 * support needs to reproduce a bug.
 *
 * Renders nothing. Mount once inside ApiProvider.
 */
import { useEffect } from 'react'
import { setUser, isMonitoringInitialized } from '@revalize/monitoring/frontend'
import { useApi } from '@/hooks/useAppApi'

export function SentryUserSync(): null {
  const { user, isAuthenticated } = useApi()

  useEffect((): void => {
    // Dev and any env without a Sentry DSN skip `initializeMonitoring()`
    // entirely, and calling `setUser` before init throws. Check first so
    // this component is a no-op in unconfigured environments.
    if (!isMonitoringInitialized()) return

    if (isAuthenticated && user) {
      setUser({
        id: user.id,
        ...(user.email ? { email: user.email } : {}),
        ...(user.tenantId ? { tenantId: user.tenantId } : {}),
      })
    } else {
      // Clear the user on sign-out so subsequent anonymous errors aren't
      // mis-attributed to the last signed-in user.
      setUser(null)
    }
  }, [user, isAuthenticated])

  return null
}
